import React, { useState, useEffect } from 'react';
import asignacionUsuarioProyectoService from '../services/asignacionUsuarioProyectoService';
import Toast from './Toast';

const AsignacionUsuarioProyectoModal = ({ isOpen, onClose, empresaId, onSuccess }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState('');
  const [selectedProyecto, setSelectedProyecto] = useState('');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroProyecto, setFiltroProyecto] = useState('');
  
  // Estados para mensajes toast
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');

  useEffect(() => {
    if (isOpen && empresaId) {
      cargarDatos();
    }
  }, [isOpen, empresaId]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      console.log('🔄 Cargando datos del modal...');
      
      const [usuariosResponse, proyectosResponse, asignacionesResponse] = await Promise.all([
        asignacionUsuarioProyectoService.obtenerUsuariosDisponibles(empresaId),
        asignacionUsuarioProyectoService.obtenerProyectosDisponibles(empresaId),
        asignacionUsuarioProyectoService.obtenerAsignaciones(empresaId)
      ]);

      if (usuariosResponse.success) {
        setUsuarios(usuariosResponse.data);
        console.log('👥 Usuarios cargados:', usuariosResponse.data);
      } else {
        mostrarMensaje('error', 'Error al cargar usuarios');
      }

      if (proyectosResponse.success) {
        setProyectos(proyectosResponse.data);
        console.log('🏗️ Proyectos cargados:', proyectosResponse.data);
      } else {
        mostrarMensaje('error', 'Error al cargar proyectos');
      }

      if (asignacionesResponse.success) {
        setAsignaciones(asignacionesResponse.data);
        console.log('📋 Asignaciones cargadas:', asignacionesResponse.data);
      } else {
        mostrarMensaje('error', 'Error al cargar asignaciones');
      }

    } catch (error) {
      console.error('❌ Error al cargar datos del modal:', error);
      mostrarMensaje('error', 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const mostrarMensaje = (tipo, texto) => {
    setToastType(tipo);
    setToastMessage(texto);
  };

  const handleToastClose = () => {
    setToastMessage('');
  };

  const crearAsignacion = async () => {
    if (!selectedUsuario || !selectedProyecto) {
      mostrarMensaje('error', 'Debes seleccionar un usuario y un proyecto');
      return;
    }

    // Verificar si ya existe la asignación
    const yaExiste = asignaciones.some(asignacion => 
      asignacion.usuario.id === parseInt(selectedUsuario) && 
      asignacion.proyectos.some(p => p.id === parseInt(selectedProyecto))
    );

    if (yaExiste) {
      mostrarMensaje('error', 'Esta asignación ya existe');
      return;
    }

    try {
      setLoading(true);
      const response = await asignacionUsuarioProyectoService.crearAsignacion(
        empresaId, 
        selectedUsuario, 
        selectedProyecto
      );

      if (response.success) {
        mostrarMensaje('success', 'Asignación creada exitosamente');
        setSelectedUsuario('');
        setSelectedProyecto('');
        await cargarDatos(); // Recargar datos
        onSuccess('Asignación creada exitosamente');
      } else {
        mostrarMensaje('error', response.error || 'Error al crear asignación');
      }
    } catch (error) {
      console.error('❌ Error al crear asignación:', error);
      mostrarMensaje('error', 'Error al crear asignación');
    } finally {
      setLoading(false);
    }
  };

  const eliminarAsignacion = async (asignacionId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta asignación?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await asignacionUsuarioProyectoService.eliminarAsignacion(asignacionId);

      if (response.success) {
        mostrarMensaje('success', 'Asignación eliminada exitosamente');
        await cargarDatos(); // Recargar datos
        onSuccess('Asignación eliminada exitosamente');
      } else {
        mostrarMensaje('error', response.error || 'Error al eliminar asignación');
      }
    } catch (error) {
      console.error('❌ Error al eliminar asignación:', error);
      mostrarMensaje('error', 'Error al eliminar asignación');
    } finally {
      setLoading(false);
    }
  };

  const usuariosFiltrados = usuarios.filter(usuario =>
    usuario.nombre.toLowerCase().includes(filtroUsuario.toLowerCase()) ||
    usuario.email.toLowerCase().includes(filtroUsuario.toLowerCase())
  );

  const proyectosFiltrados = proyectos.filter(proyecto =>
    proyecto.nombre.toLowerCase().includes(filtroProyecto.toLowerCase()) ||
    proyecto.codigo.toLowerCase().includes(filtroProyecto.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Header del Modal */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                <span className="text-2xl mr-3">👥</span>
                Gestionar Asignaciones Usuario-Proyecto
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="py-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Cargando...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Formulario para crear nueva asignación */}
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <span className="text-xl mr-2">➕</span>
                      Nueva Asignación
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Usuario
                        </label>
                        <select
                          value={selectedUsuario}
                          onChange={(e) => setSelectedUsuario(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Seleccionar usuario...</option>
                          {usuarios.map((usuario) => (
                            <option key={usuario.id} value={usuario.id}>
                              {usuario.nombre} - {usuario.cargo}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Proyecto
                        </label>
                        <select
                          value={selectedProyecto}
                          onChange={(e) => setSelectedProyecto(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Seleccionar proyecto...</option>
                          {proyectos.map((proyecto) => (
                            <option key={proyecto.id} value={proyecto.id}>
                              {proyecto.nombre} ({proyecto.codigo})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="flex items-end">
                        <button
                          onClick={crearAsignacion}
                          disabled={loading || !selectedUsuario || !selectedProyecto}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Crear Asignación
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Lista de asignaciones existentes */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <span className="text-xl mr-2">📋</span>
                      Asignaciones Existentes ({asignaciones.length})
                    </h4>

                    {/* Filtros */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Filtrar por usuario..."
                          value={filtroUsuario}
                          onChange={(e) => setFiltroUsuario(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Filtrar por proyecto..."
                          value={filtroProyecto}
                          onChange={(e) => setFiltroProyecto(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {asignaciones.length > 0 ? (
                      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                        <div className="space-y-2 p-4">
                          {asignaciones.map((asignacion) => (
                            <div key={asignacion.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="text-blue-600 font-medium text-xs">
                                        {asignacion.usuario.nombre.split(' ').map(n => n[0]).join('').toUpperCase()}
                                      </span>
                                    </div>
                                    <div>
                                      <h5 className="font-medium text-gray-900">{asignacion.usuario.nombre}</h5>
                                      <p className="text-sm text-gray-500">{asignacion.usuario.cargo}</p>
                                      <p className="text-xs text-gray-400">{asignacion.usuario.email}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="ml-11">
                                    <p className="text-sm font-medium text-gray-700 mb-1">
                                      Proyectos asignados ({asignacion.proyectos.length}):
                                    </p>
                                    {asignacion.proyectos.length > 0 ? (
                                      <div className="flex flex-wrap gap-2">
                                        {asignacion.proyectos.map((proyecto) => (
                                          <span key={proyecto.id} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                            {proyecto.nombre} ({proyecto.codigo})
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-500 italic">Sin proyectos asignados</span>
                                    )}
                                  </div>
                                </div>
                                
                                <button
                                  onClick={() => eliminarAsignacion(asignacion.id)}
                                  className="text-red-500 hover:text-red-700 transition-colors p-1"
                                  title="Eliminar asignación"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p>No hay asignaciones configuradas</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer del Modal */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toast
        message={toastMessage}
        type={toastType}
        onClose={handleToastClose}
        position="top-right"
        duration={3000}
      />
    </>
  );
};

export default AsignacionUsuarioProyectoModal;